import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye, EyeOff, AlertTriangle, Scale, Heart, Zap, ArrowRight,
  Database, DollarSign, ShieldOff, Clock, Users, Brain, Lightbulb, ChevronDown
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PhilosophyPage: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="h-16" />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="relative bg-navy-950 text-white overflow-hidden">
        {/* 배경 노이즈 패턴 */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-28 relative">
          <div className="inline-flex items-center bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm mb-8 text-gray-300">
            <Brain className="h-4 w-4 mr-2 text-teal-400" />
            CHAIR의 철학적 선언
          </div>

          <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-8">
            우리는 AI 시대를{' '}
            <span className="text-teal-400 md:block">인간의 속도로</span>{' '}
            걷고자 합니다
          </h1>

          <p className="text-xl text-gray-300 max-w-2xl leading-relaxed mb-10">
            협력은 아름다운 말입니다. 그러나 협력이 진정한 협력이 되려면
            먼저 무엇과 협력하는지를 <strong className="text-white">알아야</strong> 합니다.
            우리는 그 앎(awareness)에서 시작합니다.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#awareness"
              className="inline-flex items-center px-6 py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors"
            >
              인식의 문제부터
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
            <a
              href="#role"
              className="inline-flex items-center px-6 py-3 bg-white/10 text-white font-medium rounded-lg hover:bg-white/20 transition-colors border border-white/20"
            >
              CHAIR의 역할
            </a>
          </div>
        </div>
      </section>

      {/* ── 불편한 진실 ─────────────────────────────────────────── */}
      <section id="awareness" className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-6">
              <AlertTriangle className="h-7 w-7 text-red-600" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              AI 시대의 불편한 진실
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              AI와 협력을 논하기 전에, 우리가 마주해야 할 현실이 있습니다.
            </p>
          </div>

          {/* 진실 카드 3개 */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-2xl p-7 border border-red-100 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="bg-red-50 rounded-full p-2.5 mr-3">
                  <EyeOff className="h-5 w-5 text-red-500" />
                </div>
                <h3 className="font-bold text-gray-900">보이지 않는 수집</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                AI 시스템은 당신이 브라우저에서 머문 시간, 스크롤 속도, 검색 패턴,
                위치 이동 경로를 수집합니다. 대부분의 사람들은 이 사실을
                <strong> 약관에 동의하는 순간 이미 수락</strong>했지만 읽지 않았습니다.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-orange-100 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="bg-orange-50 rounded-full p-2.5 mr-3">
                  <Database className="h-5 w-5 text-orange-500" />
                </div>
                <h3 className="font-bold text-gray-900">프로파일링</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                개별 데이터는 사소해 보이지만, AI는 수천 개의 데이터 포인트를 결합해
                당신이 어떤 광고에 반응하는지, 어떤 정치적 메시지에
                <strong> 취약한지</strong>를 예측합니다.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-yellow-100 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="bg-yellow-50 rounded-full p-2.5 mr-3">
                  <DollarSign className="h-5 w-5 text-yellow-600" />
                </div>
                <h3 className="font-bold text-gray-900">민주주의의 위협</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                AI 기반 마이크로타겟팅은 유권자 A에게는 일자리 보호 메시지를,
                유권자 B에게는 불안을 자극하는 메시지를 보냅니다.
                <strong> 공유된 현실이 무너질 때</strong> 민주주의도 흔들립니다.
              </p>
            </div>
          </div>

          {/* 핵심 모순 — 시각적 강조 */}
          <div className="relative bg-navy-900 text-white rounded-2xl p-10 overflow-hidden">
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: 'linear-gradient(45deg, #4f46e5 25%, transparent 25%), linear-gradient(-45deg, #4f46e5 25%, transparent 25%)',
                backgroundSize: '8px 8px',
              }}
            />
            <div className="relative">
              <div className="flex items-center mb-6">
                <Scale className="h-8 w-8 text-yellow-400 mr-3" />
                <h3 className="text-2xl font-bold">핵심 모순</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                  <div className="flex items-center mb-3">
                    <ShieldOff className="h-5 w-5 text-red-400 mr-2" />
                    <span className="font-semibold text-red-300">AI 기업이 선언하는 것</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    "우리는 당신의 개인정보를 보호합니다.
                    데이터는 안전하게 관리됩니다."
                  </p>
                </div>

                <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                  <div className="flex items-center mb-3">
                    <DollarSign className="h-5 w-5 text-green-400 mr-2" />
                    <span className="font-semibold text-green-300">AI 기업이 실제로 하는 것</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    그 동일한 개인정보로 모델을 학습시키고,
                    더 정교한 서비스를 만들어 더 많은 수익을 창출합니다.
                  </p>
                </div>
              </div>

              <p className="text-gray-300 text-base leading-relaxed border-t border-white/10 pt-6">
                이 모순은 AI 기업의 악의에서 비롯된 것이 아닐 수 있습니다.
                그러나 <strong className="text-white">구조적 이해충돌</strong>이 존재하는 한,
                "우리를 믿어달라"는 말만으로는 충분하지 않습니다.
                신뢰는 선언이 아니라 검증 가능한 구조에서 나옵니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 우리의 입장 ─────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-50 rounded-full mb-6">
              <Lightbulb className="h-7 w-7 text-teal-600" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              그렇다면 우리는 어디에 서 있는가
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              두 개의 극단 사이에서, CHAIR는 제3의 길을 모색합니다.
            </p>
          </div>

          {/* 스펙트럼 시각화 */}
          <div className="relative mb-16">
            {/* 모바일: 세로 스택 / 데스크탑: 가로 3열 */}
            <div className="flex flex-col md:flex-row md:items-stretch gap-4">
              {/* 왼쪽: 모라토리움 */}
              <div className="flex-1 bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <div className="text-3xl mb-3">🛑</div>
                <h3 className="font-bold text-red-800 mb-2">개발 중단론</h3>
                <p className="text-sm text-red-700 leading-relaxed">
                  데이터센터 건설 모라토리움 등 AI 개발 자체를 일시 중단해야 한다는 주장.
                  문제의식은 타당하나, 기술 발전 우선 정책 환경에서는 현실적 영향력이 제한적입니다.
                </p>
              </div>

              {/* 구분선 — 모바일 가로선 / 데스크탑 세로선 */}
              <div className="flex md:flex-col items-center justify-center flex-shrink-0 md:px-2">
                <div className="h-0.5 w-full md:h-full md:w-0.5 bg-gradient-to-r md:bg-gradient-to-b from-red-300 via-indigo-400 to-green-300 rounded-full" />
              </div>

              {/* 중간: CHAIR */}
              <div className="flex-[1.4] bg-teal-500 text-white rounded-2xl p-7 text-center shadow-xl relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  CHAIR의 위치
                </div>
                <div className="text-3xl mb-3">🧭</div>
                <h3 className="font-bold text-lg mb-2">인식 기반 협력론</h3>
                <p className="text-sm text-teal-100 leading-relaxed">
                  AI를 막는 것도, 무조건 따라가는 것도 아닙니다.
                  먼저 <strong className="text-white">알고</strong>, 그 앎 위에서
                  <strong className="text-white"> 인간의 속도</strong>로 협력합니다.
                  이를 위한 기술·교육·제도적 기반을 연구합니다.
                </p>
              </div>

              <div className="flex md:flex-col items-center justify-center flex-shrink-0 md:px-2">
                <div className="h-0.5 w-full md:h-full md:w-0.5 bg-gradient-to-r md:bg-gradient-to-b from-indigo-400 to-green-300 rounded-full" />
              </div>

              {/* 오른쪽: 무조건 수용 */}
              <div className="flex-1 bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
                <div className="text-3xl mb-3">🚀</div>
                <h3 className="font-bold text-green-800 mb-2">무조건 수용론</h3>
                <p className="text-sm text-green-700 leading-relaxed">
                  AI가 가져올 혜택을 위해 데이터 활용과 프라이버시 침해를 감수해야 한다는 입장.
                  기술 발전의 이익이 위험보다 크다고 봅니다.
                </p>
              </div>
            </div>
          </div>

          {/* 인간의 속도란 */}
          <div className="bg-gray-50 rounded-2xl p-10">
            <div className="flex items-start mb-6">
              <div className="bg-teal-50 rounded-full p-3 mr-4 flex-shrink-0">
                <Clock className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">"인간의 속도"란 무엇인가</h3>
                <p className="text-gray-600 leading-relaxed">
                  AI는 초당 수백만 번의 계산을 합니다. 그러나 인간이 새로운 기술을
                  이해하고, 그 사회적 함의를 성찰하고, 윤리적 합의를 도출하는 데에는
                  훨씬 더 많은 시간이 필요합니다.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              {[
                {
                  step: '01',
                  label: '인식 (Awareness)',
                  desc: 'AI가 무엇을 수집하고, 어떻게 사용하는지를 일반 시민이 이해할 수 있는 수준으로 알리는 것. 기술이 아닌 사람에게 맞춘 설명.',
                  color: 'bg-blue-50 border-blue-200',
                  badge: 'text-blue-700 bg-blue-100',
                },
                {
                  step: '02',
                  label: '성찰 (Reflection)',
                  desc: '우리가 원하는 AI와의 관계가 무엇인지 사회적으로 토론하는 것. 기술 전문가만이 아닌 교육자·윤리학자·시민이 참여하는 과정.',
                  color: 'bg-purple-50 border-purple-200',
                  badge: 'text-purple-700 bg-purple-100',
                },
                {
                  step: '03',
                  label: '협력 (Collaboration)',
                  desc: '인식과 성찰 위에서 맺는 협력. AI가 인간을 대체하는 것이 아니라, 인간의 의도와 가치를 실현하는 도구로 자리 잡는 것.',
                  color: 'bg-teal-50/60 border-teal-200',
                  badge: 'text-teal-700 bg-teal-50',
                },
              ].map(({ step, label, desc, color, badge }) => (
                <div key={step} className={`rounded-xl p-6 border ${color}`}>
                  <span className={`inline-block text-xs font-bold px-2 py-1 rounded ${badge} mb-3`}>
                    STEP {step}
                  </span>
                  <h4 className="font-bold text-gray-900 mb-2">{label}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CHAIR의 역할 ────────────────────────────────────────── */}
      <section id="role" className="py-24 bg-navy-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">CHAIR가 해야 할 일</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              이 복잡한 지점에서 연구센터는 무엇을 해야 하는가.
              우리는 인간 편에 서서, 다음의 과제를 수행합니다.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                icon: Eye,
                title: 'AI 리터러시 교육',
                desc: '데이터 수집의 실제 범위, 프로파일링의 작동 방식, 개인정보 보호 선언의 한계를 일반 시민과 학생이 이해할 수 있도록 교육합니다. 기술 공포도, 맹목적 수용도 아닌 informed consent(충분한 이해에 기반한 동의)를 목표로 합니다.',
                color: 'text-blue-400',
                bg: 'bg-blue-400/10',
              },
              {
                icon: Scale,
                title: 'AI 윤리·신뢰성 연구',
                desc: '수익성과 프라이버시 보호가 상충하는 구조적 문제를 분석하고, 검증 가능한 신뢰 기준을 제시합니다. "우리를 믿어달라"가 아닌 감사 가능한(auditable) AI 시스템의 기준을 연구합니다.',
                color: 'text-yellow-400',
                bg: 'bg-yellow-400/10',
              },
              {
                icon: Users,
                title: '인간 중심 협력 모델 개발',
                desc: 'AI가 인간의 속도에 맞추도록 설계하는 방법론을 연구합니다. 효율보다 이해를, 자동화보다 공동 결정을 우선시하는 인간-AI 상호작용 패턴을 개발합니다.',
                color: 'text-green-400',
                bg: 'bg-green-400/10',
              },
              {
                icon: Zap,
                title: '정책·제도 어젠다 형성',
                desc: '기술 발전 우선 정책 환경에서도 현실적으로 작동 가능한 데이터 권리 기준과 AI 거버넌스 모델을 제안합니다. 극단적 규제가 아닌, 한국 사회에 맞는 awareness 기반 정책 어젠다를 형성합니다.',
                color: 'text-purple-400',
                bg: 'bg-purple-400/10',
              },
            ].map(({ icon: Icon, title, desc, color, bg }) => (
              <div
                key={title}
                className="flex items-start gap-5 bg-white/5 border border-white/10 rounded-xl p-7 hover:bg-white/8 transition-colors"
              >
                <div className={`flex-shrink-0 w-12 h-12 rounded-full ${bg} flex items-center justify-center`}>
                  <Icon className={`h-6 w-6 ${color}`} />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">{title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 자주 묻는 질문 ───────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">자주 묻는 질문</h2>
            <p className="text-gray-500">AI와 프라이버시에 대해 사람들이 잘 모르고 지나치는 것들</p>
          </div>

          <div className="space-y-3">
            {[
              {
                q: "AI 서비스를 무료로 쓰는데, 내 데이터가 수집된다고요?",
                a: "네. '무료 서비스'는 대부분 데이터로 지불하는 구조입니다. 당신의 사용 패턴, 질문 내용, 반응 방식은 모델 개선과 광고 타겟팅에 활용됩니다. '무료'란 돈이 아닌 데이터로 거래한다는 의미입니다.",
              },
              {
                q: "AI에게 하는 개인적인 이야기도 학습에 사용되나요?",
                a: "서비스별로 다르지만, 많은 AI 서비스가 대화 내용을 모델 개선에 사용합니다. 일부 서비스는 옵트아웃 옵션을 제공하지만, 기본값(default)은 수집 허용인 경우가 대부분입니다. 약관을 확인하고 설정에서 직접 변경해야 합니다.",
              },
              {
                q: "AI 기업들이 '프라이버시 보호'를 강조하는데, 믿을 수 있나요?",
                a: "의심은 건강한 반응입니다. AI 기업의 수익 모델이 데이터 활용에 기반하는 한, 구조적 이해충돌이 존재합니다. 선언보다 중요한 것은 독립적인 감사(audit)가 가능한지, 데이터 삭제 권리가 보장되는지, 위반 시 실질적 제재가 있는지입니다.",
              },
              {
                q: "개인이 할 수 있는 것이 있나요?",
                a: "있습니다. ① 주요 AI 서비스의 데이터 사용 설정을 확인하고 필요시 옵트아웃. ② 민감한 정보는 AI에게 공유하지 않는 습관. ③ 브라우저 개인정보 설정 강화. 그러나 개인의 노력만으로는 한계가 있습니다. 제도적 변화가 병행되어야 합니다.",
              },
              {
                q: "한국에서는 이 문제가 얼마나 심각한가요?",
                a: "한국은 AI 기술 발전 우선 정책 환경으로 인해 규제보다 산업 육성에 무게가 실려 있습니다. 개인정보보호법이 있지만 AI 시대의 실질적 보호막 역할은 아직 미흡합니다. 기술 경쟁력과 시민 권리를 동시에 추구하는 균형 있는 접근이 필요합니다.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900 pr-4">{item.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${
                      expandedFaq === idx ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedFaq === idx && (
                  <div className="px-6 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 마무리 CTA ──────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-teal-500 to-navy-800 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="h-12 w-12 mx-auto mb-6 text-teal-200" />
          <h2 className="text-3xl font-bold mb-5">
            AI는 피할 수 없습니다.<br />
            그러나 AI에 끌려갈 필요도 없습니다.
          </h2>
          <p className="text-teal-100 text-lg mb-8 leading-relaxed">
            CHAIR는 AI 기술의 빠른 발전 속에서,
            사람들이 충분히 이해하고 선택할 수 있는 공간을 만들기 위해 연구합니다.
            협력은 대등한 이해에서 시작됩니다.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/about"
              className="inline-flex items-center px-6 py-3 bg-white text-teal-700 font-semibold rounded-lg hover:bg-teal-50/60 transition-colors"
            >
              센터 소개 보기
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to="/divisions"
              className="inline-flex items-center px-6 py-3 bg-white/15 text-white font-medium rounded-lg hover:bg-white/25 transition-colors border border-white/30"
            >
              연구 분과 알아보기
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PhilosophyPage;
